import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import api from '../../services/api';
import { showError, showSuccess } from '../../services/alert';

const NotaFiscalDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const notaInicial = location.state?.notaFiscal;
  const [nota, setNota] = useState(notaInicial || null);
  const [carregando, setCarregando] = useState(!notaInicial);
  const [copiando, setCopiando] = useState(false);

  const carregarNota = async () => {
    setCarregando(true);
    try {
      const response = await api.get(`/notafiscal/${id}`);
      setNota(response.data);
    } catch (error) {
      console.error('Erro ao carregar nota fiscal.', error);
      const status = error.response?.status;
      if (status === 404) {
        showError('Nota fiscal não encontrada.');
        navigate('/financeiro/notas-fiscais');
        return;
      }

      showError('Não foi possível carregar a nota fiscal.');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!notaInicial) {
      carregarNota();
    }
  }, [id]);

  const lancamento = useMemo(() => nota?.receita || nota?.despesa || null, [nota]);
  const tipoLancamento = nota?.receita ? 'Receita' : 'Despesa';

  const formatCurrency = (valor) => {
    const numero = Number(valor || 0);
    return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (valor) => {
    if (!valor) return '-';
    const data = new Date(valor);
    return Number.isNaN(data.getTime()) ? '-' : data.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (valor) => {
    if (!valor) return '-';
    const data = new Date(valor);
    return Number.isNaN(data.getTime())
      ? '-'
      : `${data.toLocaleDateString('pt-BR')} ${data.toLocaleTimeString('pt-BR')}`;
  };

  const formatChave = (chave) => {
    if (!chave) return '-';
    return chave.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatAmbiente = (ambiente) => {
    if (!ambiente) return '-';
    return ambiente === 'Producao' ? 'Produção' : 'Homologação';
  };

  const handleCopiarChave = async () => {
    if (!nota?.chaveAcesso) return;

    if (!navigator?.clipboard?.writeText) {
      showError('Copie manualmente a chave de acesso.');
      return;
    }

    setCopiando(true);
    try {
      await navigator.clipboard.writeText(nota.chaveAcesso);
      showSuccess('Chave de acesso copiada para a área de transferência.');
    } catch (error) {
      console.error('Erro ao copiar chave de acesso.', error);
      showError('Não foi possível copiar a chave de acesso automaticamente.');
    } finally {
      setTimeout(() => setCopiando(false), 1500);
    }
  };

  const handleDownloadXml = () => {
    if (!nota?.xmlAutorizado) {
      showError('Não há XML autorizado disponível para download.');
      return;
    }

    const blob = new Blob([nota.xmlAutorizado], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `NF-e_${nota.numero || 'arquivo'}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderStatus = (status) => {
    switch (status) {
      case 'Autorizada':
        return <span className="badge bg-success-subtle text-success fw-semibold px-3 py-2">Autorizada</span>;
      case 'Rejeitada':
        return <span className="badge bg-danger-subtle text-danger fw-semibold px-3 py-2">Rejeitada</span>;
      case 'EmProcessamento':
        return <span className="badge bg-warning-subtle text-warning fw-semibold px-3 py-2">Em processamento</span>;
      case 'Erro':
        return <span className="badge bg-danger-subtle text-danger fw-semibold px-3 py-2">Erro</span>;
      default:
        return <span className="badge bg-secondary-subtle text-secondary fw-semibold px-3 py-2">Rascunho</span>;
    }
  };

  return (
    <div className="container mt-4">
      <Header />
      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-light btn-sm d-flex align-items-center gap-2"
              onClick={() => navigate('/financeiro/notas-fiscais')}
            >
              <i className="bi bi-arrow-left text-success" />
              <span className="text-success fw-semibold">Voltar</span>
            </button>
            <div>
              <h5 className="mb-0">Detalhes da Nota Fiscal</h5>
              <small className="text-white-50">Visualize as informações da nota emitida</small>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {nota && (
              <Link
                to={`/financeiro/notas-fiscais/${id}/impressao`}
                state={{ notaFiscal: nota }}
                className="btn btn-light btn-sm text-success fw-semibold d-flex align-items-center gap-2"
              >
                <i className="bi bi-printer" />
                <span>Imprimir DANFE</span>
              </Link>
            )}
            <Link
              to="/financeiro/notas-fiscais/gerar"
              className="btn btn-light btn-sm text-success fw-semibold d-flex align-items-center gap-2"
            >
              <i className="bi bi-file-earmark-plus" />
              <span>Gerar nova nota</span>
            </Link>
          </div>
        </div>
        <div className="card-body">
          {carregando ? (
            <div className="alert alert-info" role="alert">
              Carregando dados da nota fiscal...
            </div>
          ) : !nota ? (
            <div className="alert alert-warning" role="alert">
              Não foi possível localizar a nota fiscal solicitada.
            </div>
          ) : (
            <div className="row g-4">
              <div className="col-md-4">
                <label className="text-success fw-semibold">Número</label>
                <p className="fs-5 fw-bold mb-0">{nota.numero}</p>
              </div>
              <div className="col-md-4">
                <label className="text-success fw-semibold">Data de emissão</label>
                <p className="fs-6 mb-0">{formatDate(nota.dataEmissao)}</p>
              </div>
              <div className="col-md-4">
                <label className="text-success fw-semibold">Tipo de lançamento</label>
                <p className="fs-6 mb-0">{tipoLancamento}</p>
              </div>

              <div className="col-md-4">
                <label className="text-success fw-semibold">Status na SEFAZ</label>
                <div>{renderStatus(nota.statusAutorizacao)}</div>
              </div>
              <div className="col-md-4">
                <label className="text-success fw-semibold">Ambiente</label>
                <p className="fs-6 mb-0">{formatAmbiente(nota.ambienteSefaz)}</p>
              </div>
              <div className="col-md-4">
                <label className="text-success fw-semibold">Protocolo</label>
                <p className="fs-6 mb-0">{nota.protocoloAutorizacao || '-'}</p>
              </div>

              <div className="col-md-4">
                <label className="text-success fw-semibold">Data/hora da autorização</label>
                <p className="fs-6 mb-0">{formatDateTime(nota.dataAutorizacao)}</p>
              </div>

              {nota.mensagemRetorno && (
                <div className="col-12">
                  <label className="text-success fw-semibold">Mensagem da SEFAZ</label>
                  <div className="alert alert-secondary mb-0" role="alert">
                    {nota.mensagemRetorno}
                  </div>
                </div>
              )}

              <div className="col-12">
                <label className="text-success fw-semibold">Chave de acesso</label>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <code className="bg-light px-3 py-2 rounded-3 text-wrap fs-6">
                    {formatChave(nota.chaveAcesso)}
                  </code>
                  <button
                    type="button"
                    className="btn btn-outline-success btn-sm d-flex align-items-center gap-2"
                    onClick={handleCopiarChave}
                    disabled={copiando}
                  >
                    <i className="bi bi-clipboard" />
                    <span>{copiando ? 'Copiando...' : 'Copiar chave'}</span>
                  </button>
                </div>
              </div>

              <div className="col-12 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
                  onClick={handleDownloadXml}
                  disabled={!nota.xmlAutorizado}
                >
                  <i className="bi bi-filetype-xml" />
                  <span>Baixar XML autorizado</span>
                </button>
                {!nota.xmlAutorizado && (
                  <small className="text-muted d-flex align-items-center">
                    O XML autorizado estará disponível após a autorização oficial.
                  </small>
                )}
              </div>

              <div className="col-12">
                <div className="border rounded-3 p-3">
                  <h6 className="text-success fw-semibold mb-3">Lançamento vinculado</h6>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="text-muted">Identificador</label>
                      <p className="fw-semibold mb-0">#{lancamento?.id}</p>
                    </div>
                    <div className="col-md-5">
                      <label className="text-muted">Descrição</label>
                      <p className="mb-0">{lancamento?.descricao || '-'}</p>
                    </div>
                    <div className="col-md-2">
                      <label className="text-muted">Valor</label>
                      <p className="mb-0">{formatCurrency(lancamento?.valor)}</p>
                    </div>
                    <div className="col-md-2">
                      <label className="text-muted">Data</label>
                      <p className="mb-0">
                        {nota.receita
                          ? formatDate(lancamento?.dataRecebimento)
                          : formatDate(lancamento?.dataPagamento)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotaFiscalDetalhe;
